import { useState, useEffect } from "react";
import {
  Table,
  Button,
  Row,
  Col,
  Card,
  Spin,
  Input,
  Modal,
  message,
  Tag,
  Tooltip,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/es/table";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import { RiAddBoxFill } from "react-icons/ri";

import DeveloperShiftModal from "../Shift/add";

// ===== API IMPORT =====
import { getAllShift, deleteShift } from "../../utils/API";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

interface ShiftRecord {
  _id: string;
  developer_name: { _id: string; username: string }[];
  start_time: string | null;
  end_time: string | null;
  total_hours: number;
  week_off_day?: string[] | null;
}

const ShiftPage = () => {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState<ShiftRecord[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState<ShiftRecord | null>(null);
  const DAY_ORDER = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const formatHoursAndMinutes = (decimalHours: number) => {
    if (!decimalHours) return "0 hrs 0 min";

    const totalMinutes = Math.round(decimalHours * 60);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours} hrs ${minutes} min`;
  };
  // ===== FETCH DATA =====
  const fetchShiftData = async (searchQuery = "") => {
    try {
      setLoading(true);
      const response: any = await getAllShift(searchQuery);
      setDataSource(response?.data || []);
    } catch {
      message.error("Failed to fetch shifts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchShiftData(searchText);
    }, 400);

    return () => clearTimeout(delay);
  }, [searchText]);

  // ===== ADD =====
  const handleOpenAddModal = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  // ===== EDIT =====
  const handleOpenEditModal = (record: ShiftRecord) => {
    setEditData(record);
    setIsModalOpen(true);
  };

  // ===== DELETE =====
  const handleDelete = async () => {
    try {
      if (!deleteId) return;
      await deleteShift(deleteId);
      message.success("Shift deleted successfully");
      fetchShiftData();
    } catch {
      message.error("Delete failed");
    } finally {
      setDeleteId(null);
    }
  };

  // ===== MODAL SUCCESS =====
  const handleModalSuccess = () => {
    setIsModalOpen(false);
    setEditData(null);
    fetchShiftData();
  };

  // ===== SEARCH =====
  // const filteredData = dataSource.filter((item) =>
  //   item.developer_name
  // ?.map((d) => d.username)
  // .join(", ")
  // .toLowerCase().includes(searchText.toLowerCase())
  // );

  // ===== NEW TABLE INDEX =====
  const columns: ColumnType<ShiftRecord>[] = [
    {
      title: "#",
      width: 60,
      align: "center",
      render: (_, __, index) => index + 1,
    },

    {
      title: "Shift Timing",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {/* LEFT */}
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.start_time} - {record.end_time}
            </div>

            <small style={{ color: "#6b7280" }}>
              ({formatHoursAndMinutes(record.total_hours)})
            </small>
          </div>

          {/* RIGHT */}
          <div>
            {record.week_off_day?.length ? (
              <Tag
                color="red"
                style={{
                  maxWidth: 220,
                  whiteSpace: "normal",
                  lineHeight: "20px",
                  padding: "4px 8px",
                  display: "inline-block",
                }}
              >
                {record.week_off_day
                  .sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b))
                  .join(", ")}
                {" Off"}
              </Tag>
            ) : (
              <Tag color="green">Working</Tag>
            )}
          </div>
        </div>
      ),
    },

    {
      title: "Developer Name",
      dataIndex: "developer_name",
      render: (_, record) =>
        record.developer_name?.map((d) => d.username).join(", "),
    },

    {
      title: "Action",
      align: "center",
      width: 140,
      render: (_, record) => (
        <div className="flex justify-center gap-2">
          {/* EDIT BUTTON */}
          <Tooltip title="Edit">
            <button
              onClick={() => handleOpenEditModal(record)}
              //title="Edit"
              className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
        shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
        hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
        active:translate-y-[1px] active:shadow-none 
        text-white flex items-center justify-center 
        transition-all duration-200 ease-in-out group"
            >
              <FaPen
                className="group-hover:scale-110 transition-transform"
                size={11}
              />
            </button>
          </Tooltip>

          {/* DELETE BUTTON (YOUR STYLE) */}
          <Tooltip title="Delete">
            <button
              onClick={() => setDeleteId(record._id)}
              title="Delete"
              className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700
        shadow-[0_4px_12px_rgba(244,63,94,0.3)]
        hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)]
        active:translate-y-1 active:shadow-none
        text-white flex items-center justify-center
        transition-all duration-200 group"
            >
              <FaTrashAlt
                className="group-hover:scale-110 transition-transform"
                size={11}
              />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  return (
    <div className="p-2 bg-gray-100 min-h-screen">
      {/* HEADER */}
      <Row className="m-2" align="middle">
        <Col xs={24} md={8}>
          <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Shifts</h2>
        </Col>

        <Col xs={24} md={16}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
            }}
          >
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search developer..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 220 }}
              allowClear
            />

            <Button
              type="primary"
              onClick={handleOpenAddModal}
              style={{ background: "#001529", height: "33px" }}
            >
              <RiAddBoxFill /> Add
            </Button>
          </div>
        </Col>
      </Row>

      {/* TABLE */}
      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
          />
        </Spin>
      </Card>

      {/* MODAL */}
      <DeveloperShiftModal
        visible={isModalOpen}
        editData={editData}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />

      {/* DELETE MODAL */}
      <Modal
        open={!!deleteId} // <-- This makes the modal open when deleteId has a value
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        // footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Shift?</p>
      </Modal>
    </div>
  );
};

export const SHIFTS_TABLE_COLUMNS = ["start_time", "developer_name"];

export default ShiftPage;


